import { Navigate, Route, Routes } from 'react-router-dom'
import { Box, Skeleton } from '@mui/material'
import React, { Suspense } from 'react'
import { ROUTES, Route as AppRoute } from '../routes'
import ErrorPage from './ErrorPage'
import { useAppStore } from '../store/appStore'
import { isReadOnly } from '../models/UserModel'

export const flattenRoutes = (routes: typeof ROUTES): AppRoute[] =>
    Object.values(routes).flatMap((group) => Object.values(group))

// The whole of what an offboarded user may reach: their own history, the encounters behind
// it (read-only) and Settings, where a solo account renews (MD-1497). Everything else in the
// table is still routed, at their history — deny by redirect rather than by omission, since
// an omitted path is matched by a dynamic sibling instead ("/e/new" as the encounter "new").
const READ_ONLY_PATHS: ReadonlySet<string> = new Set([
    ROUTES.encounters.list.path,
    ROUTES.encounters.view.path,
    ROUTES.settings.main.path,
])

const AppRoutes: React.FC = () => {
    const templates = useAppStore((state) => state.templates)
    const templatesLoading = useAppStore((state) => state.templatesLoading)
    const userModel = useAppStore((state) => state.userModel)
    const templatesCount = Object.keys(templates).length
    const isRedirectToTemplateLibrary = templatesCount <= 1
    const defaultPath = isRedirectToTemplateLibrary ? ROUTES.templateLibrary.list.path : ROUTES.encounters.new.path

    // An offboarded user reaches their own history and the encounters behind it, where they
    // can read the reports and transcripts they made while active (MD-1497). Routed ahead of
    // the loading gate below, which is about a template list they are never served.
    if (isReadOnly(userModel)) {
        const toHistory = <Navigate to={ROUTES.encounters.list.path} replace />
        return (
            <Suspense fallback={<div></div>}>
                <Routes>
                    {flattenRoutes(ROUTES).map(({ path, page: C }) => (
                        <Route key={path} path={path} element={READ_ONLY_PATHS.has(path) ? <C /> : toHistory} />
                    ))}
                    <Route path="*" element={toHistory} />
                </Routes>
            </Suspense>
        )
    }

    if (templatesLoading && templatesCount === 0) {
        return (
            <Box sx={{ px: 3 }}>
                <Skeleton height={56} width="40%" />
                <Skeleton height={40} />
                <Skeleton height={40} width="80%" />
                <Skeleton height={40} width="60%" />
            </Box>
        )
    }

    return (
        <Suspense fallback={<div></div>}>
            <Routes>
                <Route path="/" element={<Navigate to={defaultPath} replace />} />

                {flattenRoutes(ROUTES).map(({ path, page: C }) => (
                    <Route key={path} path={path} element={<C />} />
                ))}

                <Route path="/error" element={<ErrorPage />} />
                <Route path="*" element={<Navigate to="/error" replace />} />
            </Routes>
        </Suspense>
    )
}

export default AppRoutes
